import React, { useEffect, useMemo, useState } from "react";
import {
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ResponsiveContainer,
    Area,
    AreaChart,
    Legend,
} from "recharts";
import {
    ClipboardList,
    CheckCircle2,
    Loader2,
    AlertCircle,
    TrendingUp,
    TrendingDown,
    ArrowRight,
    HardDrive,
    Monitor,
    Server,
    Radio,
    History,
    Calendar,
    Download,
} from "lucide-react";
import { Link } from "react-router-dom";
import { http, formatBytes, splitBytes, splitSpeedMbps, splitPct } from "@/lib/api";
import { MetricInline } from "@/components/Metric";
import CountUp from "@/components/CountUp";
import { useAuth } from "@/context/AuthContext";
import { useWS } from "@/context/WSContext";
import DonutChart from "@/components/DonutChart";
import RetentionForecastWidget from "@/components/RetentionForecastWidget";
import { isDesktop } from "@/lib/desktopShell";

// ---- KPI cards ----
function KpiCard({ label, value, unit, delta, deltaSuffix = "vs last 7 days", icon: Icon, iconBg, iconColor, testId }) {
    const positive = delta >= 0;
    const deltaAbs = Math.abs(delta);
    return (
        <div className="mb-kpi-modern" data-testid={testId}>
            <div className="flex items-start justify-between">
                <div className="mb-icon-badge" style={{ background: iconBg }}>
                    <Icon size={22} color={iconColor} strokeWidth={2} />
                </div>
                <div className="text-right">
                    <div className="mb-card-title text-foreground">{label}</div>
                    <div className="mt-1 flex items-baseline justify-end gap-1.5">
                        <span className="mb-metric">
                            <CountUp value={Number(value) || 0} decimals={0} />
                        </span>
                        {unit && <span className="mb-metric-unit">{unit}</span>}
                    </div>
                </div>
            </div>
            {delta !== null && delta !== undefined && (
                <div className="flex items-center gap-1.5 text-xs">
                    {positive ? (
                        <TrendingUp size={13} className="text-[hsl(var(--success))]" />
                    ) : (
                        <TrendingDown size={13} className="text-[hsl(var(--destructive))]" />
                    )}
                    <span
                        className={
                            positive
                                ? "font-semibold text-[hsl(var(--success))]"
                                : "font-semibold text-[hsl(var(--destructive))]"
                        }
                    >
                        {deltaAbs.toFixed(0)}%
                    </span>
                    <span className="text-muted-foreground">{deltaSuffix}</span>
                </div>
            )}
        </div>
    );
}

// ---- Recent job row ----
function RecentJobItem({ job }) {
    const isSuccess = job.status === "succeeded";
    const isFailed = job.status === "failed" || job.status === "completed_with_errors";
    const isRunning = job.status === "running" || job.status === "queued" || job.status === "paused";

    const Icon = isSuccess ? CheckCircle2 : isFailed ? AlertCircle : Loader2;
    const iconBg = isSuccess ? "#dcfce7" : isFailed ? "#fee2e2" : "#dbeafe";
    const iconColor = isSuccess ? "#16a34a" : isFailed ? "#dc2626" : "#2563eb";

    const pillCls = isSuccess
        ? "bg-[#dcfce7] text-[#16a34a]"
        : isFailed
          ? "bg-[#fee2e2] text-[#dc2626]"
          : "bg-[#dbeafe] text-[#2563eb]";

    const label = isSuccess ? "Completed" : isFailed ? "Failed" : "In Progress";

    const ago = (() => {
        if (!job.created_at) return "—";
        const diff = (Date.now() - new Date(job.created_at).getTime()) / 1000;
        if (diff < 60) return `${Math.floor(diff)}s ago`;
        if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
        if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
        return `${Math.floor(diff / 86400)}d ago`;
    })();

    return (
        <Link
            to={`/jobs/${job.id}`}
            className="flex items-center justify-between gap-3 rounded-lg px-3 py-2.5 transition-colors hover:bg-muted/50"
            data-testid={`recent-job-${job.id}`}
        >
            <div className="flex min-w-0 items-center gap-3">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg" style={{ background: iconBg }}>
                    <Icon size={18} color={iconColor} className={isRunning ? "animate-spin" : ""} />
                </div>
                <div className="min-w-0">
                    <div className="truncate text-sm font-semibold">{job.name}</div>
                    <div className="truncate text-[11px] text-muted-foreground">
                        {job.source_agent_name} → {job.dest_agent_name}
                    </div>
                </div>
            </div>
            <div className="flex shrink-0 flex-col items-end gap-1">
                <span className={`rounded-full px-2.5 py-0.5 text-[10px] font-semibold ${pillCls}`}>{label}</span>
                <span className="text-[10px] text-muted-foreground">{ago}</span>
            </div>
        </Link>
    );
}

// ---- Component ----
export default function Dashboard() {
    const { user } = useAuth();
    const [summary, setSummary] = useState(null);
    const [throughput, setThroughput] = useState([]);
    const [statusBreak, setStatusBreak] = useState([]);
    const [recentJobs, setRecentJobs] = useState([]);
    const [agentsById, setAgentsById] = useState({});
    const [agents, setAgents] = useState([]);
    const { subscribe } = useWS();

    const refresh = async () => {
        try {
            const [s, t, sb, jobs, ag] = await Promise.all([
                http.get("/dashboard/summary"),
                http.get("/analytics/throughput"),
                http.get("/analytics/status-breakdown"),
                http.get("/jobs"),
                http.get("/agents"),
            ]);
            setSummary(s.data);
            setThroughput(t.data);
            setStatusBreak(sb.data);
            setRecentJobs(jobs.data);
            setAgents(ag.data || []);
            const byId = {};
            (ag.data || []).forEach((a) => (byId[a.id] = a));
            setAgentsById(byId);
        } catch {
            /* ignore */
        }
    };

    useEffect(() => {
        refresh();
        const iv = setInterval(refresh, 8000);
        return () => clearInterval(iv);
    }, []);

    useEffect(() => {
        const unsub = subscribe((m) => {
            if (
                [
                    "job.progress",
                    "job.complete",
                    "job.update",
                    "job.created",
                    "alert.new",
                    "agent.heartbeat",
                ].includes(m.event)
            ) {
                refresh();
            }
        });
        return unsub;
    }, [subscribe]);

    // Build Migration Activity series: group recent jobs by day + status for last 7 days.
    const activitySeries = useMemo(() => {
        const days = 7;
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const buckets = {};
        for (let i = days - 1; i >= 0; i--) {
            const d = new Date(today);
            d.setDate(today.getDate() - i);
            const key = d.toISOString().slice(0, 10);
            buckets[key] = { date: key, completed: 0, in_progress: 0, failed: 0 };
        }
        recentJobs.forEach((j) => {
            const k = (j.created_at || "").slice(0, 10);
            if (!buckets[k]) return;
            if (j.status === "succeeded") buckets[k].completed += 1;
            else if (["running", "queued", "paused"].includes(j.status)) buckets[k].in_progress += 1;
            else if (["failed", "completed_with_errors"].includes(j.status)) buckets[k].failed += 1;
        });
        return Object.values(buckets).map((b) => ({
            ...b,
            label: new Date(b.date).toLocaleDateString(undefined, { month: "short", day: "numeric" }),
        }));
    }, [recentJobs]);

    // Compute 7-day deltas (% change vs previous 7-day window).
    const deltas = useMemo(() => {
        const now = Date.now();
        const day = 24 * 3600 * 1000;
        const within = (j, since, until) => {
            const t = new Date(j.created_at || 0).getTime();
            return t >= since && t < until;
        };
        const compute = (pred) => {
            const cur = recentJobs.filter((j) => within(j, now - 7 * day, now) && pred(j)).length;
            const prev = recentJobs.filter((j) => within(j, now - 14 * day, now - 7 * day) && pred(j)).length;
            if (prev === 0) return cur > 0 ? 100 : 0;
            return Math.round(((cur - prev) / prev) * 100);
        };
        return {
            total: compute(() => true),
            completed: compute((j) => j.status === "succeeded"),
            in_progress: compute((j) => ["running", "queued", "paused"].includes(j.status)),
            failed: compute((j) => ["failed", "completed_with_errors"].includes(j.status)),
        };
    }, [recentJobs]);

    const spotlight = useMemo(() => {
        const running = recentJobs.find((j) => j.status === "running");
        return running || recentJobs[0] || null;
    }, [recentJobs]);

    // Donut data for "Jobs by Status"
    const statusDonut = useMemo(() => {
        const colorMap = {
            succeeded: "#22c55e",
            running: "#3b82f6",
            queued: "#f59e0b",
            paused: "#94a3b8",
            completed_with_errors: "#f97316",
            failed: "#ef4444",
            canceled: "#cbd5e1",
        };
        const labelMap = {
            succeeded: "Completed",
            running: "In Progress",
            queued: "Scheduled",
            paused: "Paused",
            completed_with_errors: "Errors",
            failed: "Failed",
            canceled: "Canceled",
        };
        const out = statusBreak.map((s) => ({
            name: labelMap[s.status] || s.status,
            value: s.count,
            color: colorMap[s.status] || "#94a3b8",
        }));
        return out;
    }, [statusBreak]);

    // Agent Health donut — `stale` counts as online because the agent
    // is still within the grace window (90–180 s since last heartbeat).
    const agentDonut = useMemo(() => {
        const online = agents.filter((a) => a.status === "online" || a.status === "stale").length;
        const restarting = agents.filter((a) => a.status === "restarting").length;
        const offline = agents.filter((a) =>
            a.status !== "online" && a.status !== "stale" && a.status !== "restarting"
        ).length;
        return {
            data: [
                { name: "Online", value: online, color: "#22c55e" },
                { name: "Offline", value: offline, color: "#ef4444" },
                { name: "Updating", value: restarting, color: "#f59e0b" },
            ],
            online,
            offline,
            restarting,
            total: agents.length,
        };
    }, [agents]);

    // Top agents by transferred bytes (real data, multi-tenant-friendly later)
    const topAgents = useMemo(() => {
        const totals = {};
        recentJobs.forEach((j) => {
            const key = j.source_agent_name || "unknown";
            totals[key] = (totals[key] || 0) + (j.bytes_done || 0);
        });
        const rows = Object.entries(totals)
            .map(([name, bytes]) => ({ name, bytes }))
            .sort((a, b) => b.bytes - a.bytes)
            .slice(0, 5);
        const max = Math.max(1, ...rows.map((r) => r.bytes));
        return rows.map((r) => ({ ...r, pct: Math.round((r.bytes / max) * 100) }));
    }, [recentJobs]);

    // Storage transferred metrics
    const storage = useMemo(() => {
        const transferred = summary?.total_bytes_transferred || 0;
        const queuedBytes = recentJobs
            .filter((j) => ["queued", "paused", "running"].includes(j.status))
            .reduce((a, j) => a + (j.total_bytes || 0), 0);
        const capacity = transferred + queuedBytes;
        const pct = capacity > 0 ? Math.round((transferred / capacity) * 100) : 0;
        const succ = summary?.verification?.integrity_rate ?? 100;
        const speed = summary?.current_aggregate_speed_mbps || 0;
        return {
            transferred,
            capacity,
            pct,
            successRate: succ,
            speedGbps: speed > 0 ? (speed * 8) / 1000 : 0,
        };
    }, [summary, recentJobs]);

    const greeting = (user?.name || user?.email?.split("@")[0] || "Operator");
    const canDeployAgent = user?.role === "admin" || user?.role === "superadmin";

    return (
        <div className="mx-auto max-w-[1600px] p-6">
            {/* Welcome + date range */}
            <div className="mb-6 flex flex-wrap items-end justify-between gap-3">
                <div>
                    <h1 className="mb-headline-lg">
                        Welcome back, {greeting} <span className="inline-block">👋</span>
                    </h1>
                    <p className="mt-1 text-sm text-muted-foreground">
                        Here's what's happening with your migrations today.
                    </p>
                </div>
                <div className="flex items-center gap-2">
                    <div
                        className="flex items-center gap-2 rounded-lg border border-border bg-surface px-4 py-2 text-sm"
                        data-testid="dashboard-today-pill"
                    >
                        <Calendar size={14} className="text-muted-foreground" />
                        <span className="font-medium">
                            {new Date().toLocaleDateString(undefined, {
                                weekday: "short",
                                month: "short",
                                day: "numeric",
                                year: "numeric",
                            })}
                        </span>
                    </div>
                </div>
            </div>

            {/* Getting-started nudge — every KPI/chart/donut below reads as
                a wall of zeros until at least one agent is connected, which
                is easy to mistake for "the app is broken" rather than
                "nothing's set up yet". Self-hides the moment an agent
                registers. */}
            {agents.length === 0 && (
                <div
                    className="mb-5 flex flex-col items-start gap-3 rounded-xl bg-gradient-to-r from-[#5b5ef0] to-[#7c3aed] p-5 text-white shadow-lg sm:flex-row sm:items-center sm:justify-between"
                    data-testid="dashboard-getting-started"
                >
                    <div>
                        <div className="font-display text-base font-bold">No agents connected yet</div>
                        <p className="mt-1 max-w-xl text-xs text-white/80">
                            Everything below will read zero until a MoovBytes agent is running somewhere —
                            install one on a PC, server, or NAS to start your first migration.
                        </p>
                    </div>
                    {canDeployAgent && (
                        <Link
                            to="/access-tokens?tab=deploy-agent"
                            data-testid="dashboard-download-agent-btn"
                            className="inline-flex shrink-0 items-center gap-2 rounded-lg bg-white px-4 py-2.5 text-sm font-semibold text-[#5b5ef0] shadow hover:bg-white/95"
                        >
                            <Download size={15} /> Download agent
                        </Link>
                    )}
                </div>
            )}

            {/* Row 1 — KPIs */}
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4" data-testid="kpi-row">
                <KpiCard
                    label="Total Jobs"
                    value={summary?.total_jobs ?? "—"}
                    delta={deltas.total}
                    icon={ClipboardList}
                    iconBg="#ede9fe"
                    iconColor="#7c3aed"
                    testId="kpi-total-jobs"
                />
                <KpiCard
                    label="Completed"
                    value={summary?.succeeded_jobs ?? "—"}
                    delta={deltas.completed}
                    icon={CheckCircle2}
                    iconBg="#dcfce7"
                    iconColor="#16a34a"
                    testId="kpi-completed"
                />
                <KpiCard
                    label="In Progress"
                    value={summary?.active_jobs ?? "—"}
                    delta={deltas.in_progress}
                    icon={Loader2}
                    iconBg="#dbeafe"
                    iconColor="#2563eb"
                    testId="kpi-in-progress"
                />
                <KpiCard
                    label="Failed"
                    value={summary?.failed_jobs ?? "—"}
                    delta={deltas.failed}
                    icon={AlertCircle}
                    iconBg="#ffedd5"
                    iconColor="#ea580c"
                    testId="kpi-failed"
                />
            </div>

            {/* Row 2 — Migration Activity + Recent Jobs */}
            <div className="mt-5 grid grid-cols-1 gap-4 lg:grid-cols-3">
                <div className="mb-card lg:col-span-2" data-testid="migration-activity-card">
                    <div className="mb-4 flex items-center justify-between">
                        <div>
                            <h3 className="font-display text-lg font-bold">Migration Activity</h3>
                            <div className="mt-3 flex items-center gap-4 text-[11px]">
                                <LegendDot color="#22c55e" label="Completed" />
                                <LegendDot color="#3b82f6" label="In Progress" />
                                <LegendDot color="#ef4444" label="Failed" />
                            </div>
                        </div>
                        <div
                            className="rounded-lg border border-border bg-background px-3 py-1.5 text-xs font-medium"
                            data-testid="migration-activity-window"
                        >
                            Past 7 days
                        </div>
                    </div>
                    <div className="h-[260px] min-h-[200px] w-full">
                        <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={200}>
                            <AreaChart data={activitySeries} margin={{ top: 8, right: 8, left: -8, bottom: 0 }}>
                                <defs>
                                    <linearGradient id="gComp" x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="0%" stopColor="#22c55e" stopOpacity={0.35} />
                                        <stop offset="100%" stopColor="#22c55e" stopOpacity={0} />
                                    </linearGradient>
                                    <linearGradient id="gProg" x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="0%" stopColor="#3b82f6" stopOpacity={0.3} />
                                        <stop offset="100%" stopColor="#3b82f6" stopOpacity={0} />
                                    </linearGradient>
                                    <linearGradient id="gFail" x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="0%" stopColor="#ef4444" stopOpacity={0.25} />
                                        <stop offset="100%" stopColor="#ef4444" stopOpacity={0} />
                                    </linearGradient>
                                </defs>
                                <CartesianGrid strokeDasharray="3 6" stroke="hsl(var(--border))" vertical={false} />
                                <XAxis
                                    dataKey="label"
                                    tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 11 }}
                                    tickLine={false}
                                    axisLine={false}
                                />
                                <YAxis
                                    tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 11 }}
                                    tickLine={false}
                                    axisLine={false}
                                    allowDecimals={false}
                                />
                                <Tooltip
                                    contentStyle={{
                                        background: "hsl(var(--surface))",
                                        border: "1px solid hsl(var(--border))",
                                        fontSize: 12,
                                        borderRadius: 8,
                                    }}
                                />
                                <Area type="monotone" dataKey="completed" stroke="#22c55e" strokeWidth={2} fill="url(#gComp)" />
                                <Area type="monotone" dataKey="in_progress" stroke="#3b82f6" strokeWidth={2} fill="url(#gProg)" />
                                <Area type="monotone" dataKey="failed" stroke="#ef4444" strokeWidth={2} fill="url(#gFail)" />
                            </AreaChart>
                        </ResponsiveContainer>
                    </div>
                </div>

                <div className="mb-card" data-testid="recent-jobs-card">
                    <div className="mb-2 flex items-center justify-between">
                        <h3 className="font-display text-lg font-bold">Recent Jobs</h3>
                        <Link
                            to="/jobs"
                            className="text-xs font-semibold text-[#5b5ef0] transition-colors hover:text-[#4b4ef0]"
                        >
                            View All
                        </Link>
                    </div>
                    <div className="space-y-1">
                        {recentJobs.length === 0 ? (
                            <div className="py-12 text-center text-sm text-muted-foreground">
                                No jobs yet. <Link to="/jobs?create=1" className="text-[#5b5ef0] underline">Create one</Link>
                            </div>
                        ) : (
                            recentJobs.slice(0, 5).map((j) => <RecentJobItem key={j.id} job={j} />)
                        )}
                    </div>
                </div>
            </div>

            {/* Row 3 — System Pulse: live operational pulse strip.
                Replaces the old source→destination diagram with a single-line
                summary of the values an operator actually scans for: what's
                flowing right now, how fast, how trustworthy, and how the
                fleet is holding up. */}
            <div className="mt-5" data-testid="system-pulse-row">
                <SystemPulseStrip
                    agents={agents}
                    summary={summary}
                    spotlight={spotlight}
                    recentJobs={recentJobs}
                />
            </div>

            {/* Storage savings forecast — visible to engineer+; admins get the
                Apply-SOC2-defaults CTA. Self-hides on 403 or zero data.
                Hidden entirely in the Desktop app: it's the exact same
                Retention component/backend, already reachable there via
                the Client Portal's own Settings > Retention tab
                (PortalSettings.jsx), so showing it again here would just
                be a duplicate entry point for Desktop users specifically.
                Plain browser/SaaS dashboard users don't get this same
                Portal nav prominence, so it stays visible for them. */}
            {!isDesktop() && (
                <div className="mt-5" data-testid="retention-forecast-row">
                    <RetentionForecastWidget />
                </div>
            )}

            {/* Row 4 — Donut row */}
            <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4" data-testid="donut-row">
                {/* Jobs by Status */}
                <div className="mb-card">
                    <h3 className="mb-3 font-display text-base font-bold">Jobs by Status</h3>
                    <DonutChart
                        data={statusDonut}
                        innerRadius={46}
                        outerRadius={70}
                        height={150}
                        testId="jobs-status-donut"
                    />
                    <div className="mt-3 space-y-1.5">
                        {statusDonut.slice(0, 4).map((s) => {
                            const tot = statusDonut.reduce((a, b) => a + b.value, 0);
                            const pct = tot ? ((s.value / tot) * 100).toFixed(1) : "0.0";
                            return (
                                <div key={s.name} className="flex items-center gap-2 text-[11px]">
                                    <span className="h-2 w-2 rounded-full" style={{ background: s.color }} />
                                    <span className="flex-1 truncate text-muted-foreground">{s.name}</span>
                                    <span className="font-semibold tabular-nums">
                                        {s.value} <span className="text-muted-foreground">({pct}%)</span>
                                    </span>
                                </div>
                            );
                        })}
                    </div>
                </div>

                {/* Top Agents */}
                <div className="mb-card">
                    <div className="mb-3 flex items-center justify-between">
                        <h3 className="font-display text-base font-bold">Top Agents</h3>
                        <Link to="/agents" className="text-[11px] font-semibold text-[#5b5ef0]">
                            View All
                        </Link>
                    </div>
                    <div className="space-y-3 pt-2">
                        {topAgents.length === 0 ? (
                            <div className="py-8 text-center text-xs text-muted-foreground">No transfer data yet</div>
                        ) : (
                            topAgents.map((a) => (
                                <div key={a.name} className="flex items-center gap-2 text-[11px]">
                                    <Radio size={12} className="shrink-0 text-[#5b5ef0]" />
                                    <span className="w-20 shrink-0 truncate font-medium">{a.name}</span>
                                    <div className="flex-1">
                                        <div className="h-1.5 rounded-full bg-muted">
                                            <div
                                                className="h-full rounded-full bg-gradient-to-r from-[#5b5ef0] to-[#7c5cff]"
                                                style={{ width: `${a.pct}%` }}
                                            />
                                        </div>
                                    </div>
                                    <span className="w-14 shrink-0 text-right font-mono tabular-nums">
                                        {formatBytes(a.bytes)}
                                    </span>
                                </div>
                            ))
                        )}
                    </div>
                </div>

                {/* Storage Transferred */}
                <div className="mb-card">
                    <h3 className="mb-3 font-display text-base font-bold">Storage Transferred</h3>
                    <DonutChart
                        data={[
                            { name: "Transferred", value: storage.pct, color: "#5b5ef0" },
                            { name: "Remaining", value: Math.max(0, 100 - storage.pct), color: "#e8ecff" },
                        ]}
                        centerValue={`${storage.pct}%`}
                        innerRadius={46}
                        outerRadius={70}
                        height={150}
                        testId="storage-donut"
                    />
                    <div className="mt-3 grid grid-cols-2 gap-3 text-[11px]">
                        <div>
                            <div className="mb-card-title text-muted-foreground">Total Transferred</div>
                            {(() => { const s = splitBytes(storage.transferred);
                                return <MetricInline value={s.value} unit={s.unit} size="sm" decimals={s.display.includes(".") ? (s.display.split(".")[1].length) : 0} />;
                            })()}
                        </div>
                        <div>
                            <div className="mb-card-title text-muted-foreground">Success Rate</div>
                            <MetricInline value={storage.successRate} unit="%" size="sm" decimals={1} />
                        </div>
                        {storage.speedGbps > 0 && (
                            <div className="col-span-2">
                                <div className="mb-card-title text-muted-foreground">Avg. Speed</div>
                                <MetricInline value={storage.speedGbps} unit="Gbps" size="sm" decimals={2} />
                            </div>
                        )}
                    </div>
                </div>

                {/* Agent Health */}
                <div className="mb-card">
                    <div className="mb-3 flex items-center justify-between">
                        <h3 className="font-display text-base font-bold">Agent Health</h3>
                        <Link to="/agents" className="text-[11px] font-semibold text-[#5b5ef0]">
                            View All
                        </Link>
                    </div>
                    <DonutChart
                        data={agentDonut.data}
                        centerValue={agentDonut.online}
                        centerLabel="Online"
                        innerRadius={46}
                        outerRadius={70}
                        height={150}
                        testId="agents-donut"
                    />
                    <div className="mt-3 space-y-1.5 text-[11px]">
                        <AgentRow color="#22c55e" label="Online" value={agentDonut.online} />
                        <AgentRow color="#ef4444" label="Offline" value={agentDonut.offline} />
                        <AgentRow color="#f59e0b" label="Updating" value={agentDonut.restarting} />
                        <div className="mt-2 flex justify-between border-t border-border pt-2 text-muted-foreground">
                            <span>Total Agents</span>
                            <span className="font-semibold text-foreground">{agentDonut.total}</span>
                        </div>
                    </div>
                </div>
            </div>

            {/* Row 5 — Platform Activity: migration mix + schedules + audit feed */}
            <div className="mt-5 grid grid-cols-1 gap-4 lg:grid-cols-3" data-testid="platform-activity-row">
                <MigrationMixCard mix={summary?.migration_mix} />
                <SchedulesCard active={summary?.schedules_active ?? 0} />
                <AuditFeedCard events={summary?.recent_audit || []} />
            </div>
        </div>
    );
}

function MigrationMixCard({ mix }) {
    const pc = mix?.pc ?? 0;
    const server = mix?.server ?? 0;
    const storage = mix?.storage ?? 0;
    const total = pc + server + storage;
    const pct = (n) => (total ? Math.round((n / total) * 100) : 0);
    return (
        <div className="mb-card" data-testid="migration-mix-card">
            <div className="mb-3 flex items-center justify-between">
                <h3 className="font-display text-base font-bold">Migration Mix</h3>
                <Link to="/migrate" className="text-[11px] font-semibold text-[#5b5ef0]">
                    New →
                </Link>
            </div>
            {total === 0 ? (
                <div className="py-10 text-center text-xs text-muted-foreground">
                    No migrations yet.{" "}
                    <Link to="/migrate" className="text-[#5b5ef0] underline">
                        Start one
                    </Link>
                </div>
            ) : (
                <div className="space-y-2.5">
                    <MixRow
                        icon={Monitor}
                        tint="#5b5ef0"
                        label="PC → PC"
                        count={pc}
                        pct={pct(pc)}
                        to="/migrations"
                    />
                    <MixRow
                        icon={Server}
                        tint="#7c5cff"
                        label="Server → Server"
                        count={server}
                        pct={pct(server)}
                        to="/migrate/server"
                    />
                    <MixRow
                        icon={HardDrive}
                        tint="#00C2FF"
                        label="Storage / NAS"
                        count={storage}
                        pct={pct(storage)}
                        to="/migrate/storage"
                    />
                    <div className="mt-3 flex items-center justify-between border-t border-border pt-2 text-[11px] text-muted-foreground">
                        <span>Total migrations</span>
                        <span className="font-semibold text-foreground tabular-nums">{total}</span>
                    </div>
                </div>
            )}
        </div>
    );
}

function MixRow({ icon: Icon, tint, label, count, pct, to }) {
    return (
        <Link
            to={to}
            className="block rounded-lg border border-border bg-background/60 p-3 transition-colors hover:border-foreground/30"
            data-testid={`mix-row-${label.replace(/\W+/g, "-").toLowerCase()}`}
        >
            <div className="flex items-center gap-3">
                <div
                    className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md"
                    style={{ background: `${tint}22`, color: tint }}
                >
                    <Icon size={16} />
                </div>
                <div className="min-w-0 flex-1">
                    <div className="flex items-center justify-between text-[12px]">
                        <span className="font-semibold">{label}</span>
                        <span className="font-mono tabular-nums">
                            {count} <span className="text-muted-foreground">· {pct}%</span>
                        </span>
                    </div>
                    <div className="mt-1.5 h-1 overflow-hidden rounded-full bg-muted">
                        <div
                            className="h-full rounded-full transition-all"
                            style={{ width: `${pct}%`, background: tint }}
                        />
                    </div>
                </div>
            </div>
        </Link>
    );
}

function SchedulesCard({ active }) {
    return (
        <div className="mb-card" data-testid="schedules-card">
            <div className="mb-3 flex items-center justify-between">
                <h3 className="font-display text-base font-bold">Schedules</h3>
                <Link to="/schedules" className="text-[11px] font-semibold text-[#5b5ef0]">
                    Manage →
                </Link>
            </div>
            <div className="flex items-center gap-3">
                <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-[#5b5ef0]/10 text-[#5b5ef0]">
                    <Calendar size={20} />
                </div>
                <div>
                    <div className="font-display text-3xl font-bold tabular-nums">
                        <CountUp value={active} decimals={0} />
                    </div>
                    <div className="text-[11px] text-muted-foreground">Active schedules</div>
                </div>
            </div>
            <p className="mt-3 text-[11px] text-muted-foreground">
                Scheduled migrations fire automatically at their next run time.
            </p>
        </div>
    );
}

function AuditFeedCard({ events }) {
    return (
        <div className="mb-card" data-testid="audit-feed-card">
            <div className="mb-3 flex items-center justify-between">
                <h3 className="font-display text-base font-bold">Recent Activity</h3>
                <Link to="/audit" className="text-[11px] font-semibold text-[#5b5ef0]">
                    View Audit Log
                </Link>
            </div>
            {events.length === 0 ? (
                <div className="py-10 text-center text-xs text-muted-foreground">
                    No recent activity.
                </div>
            ) : (
                <ul className="space-y-2">
                    {events.slice(0, 6).map((e) => (
                        <li key={e.id} className="flex items-start gap-2.5 text-[11px]">
                            <History size={12} className="mt-0.5 shrink-0 text-muted-foreground" />
                            <div className="min-w-0 flex-1">
                                <div className="truncate">
                                    <span className="font-semibold">{e.actor_email || "system"}</span>
                                    {" "}
                                    <span className="text-muted-foreground">{humanizeAction(e.action)}</span>
                                    {e.entity_type && (
                                        <span className="text-muted-foreground"> · {e.entity_type}</span>
                                    )}
                                </div>
                                <div className="font-mono text-[10px] text-muted-foreground">
                                    {relativeTime(e.created_at)}
                                </div>
                            </div>
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
}

function humanizeAction(action) {
    if (!action) return "did something";
    return action.replace(/_/g, " ").replace(/\./g, " ");
}

function relativeTime(iso) {
    if (!iso) return "—";
    const diff = (Date.now() - new Date(iso).getTime()) / 1000;
    if (diff < 60) return `${Math.floor(diff)}s ago`;
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return `${Math.floor(diff / 86400)}d ago`;
}

function LegendDot({ color, label }) {
    return (
        <div className="flex items-center gap-1.5">
            <span className="h-2 w-2 rounded-full" style={{ background: color }} />
            <span className="text-muted-foreground">{label}</span>
        </div>
    );
}

// System Pulse — single-strip "how is the platform doing right now?" readout.
// Every cell is a concrete operational fact; no decorative telemetry.
function SystemPulseStrip({ agents, summary, spotlight, recentJobs }) {
    const online = agents.filter((a) => a.status === "online").length;
    const total = agents.length;
    // `|| 1` only guards the percentage division — showing it in the "x/y"
    // text too used to render "0/1" for a tenant with zero agents, reading
    // as "one agent, offline" instead of "no agents registered at all".
    const fleetPct = total > 0 ? Math.round((online / total) * 100) : 0;
    // Quantum-Safe Transfer fleet readiness. Storage connection records
    // (nas/s3/gcs/azure) never run agent software and never report
    // security_capabilities — counting them would permanently deflate the
    // ratio with devices that can never be PQC-capable, so they're excluded
    // from both the numerator and denominator (mirrors Agents.jsx's own
    // isCloud/isNas split). A pre-PQC agent build reports no
    // security_capabilities at all, which correctly reads as "not ready"
    // here rather than throwing.
    const realAgents = agents.filter((a) => !["nas", "s3", "gcs", "azure"].includes(a.kind));
    const pqcReadyCount = realAgents.filter((a) =>
        (a.security_capabilities || []).includes("QuantumSafeHybrid"),
    ).length;
    const realAgentTotal = realAgents.length;
    const runningJobs = (recentJobs || []).filter(
        (j) => ["running", "queued", "paused"].includes(j.status),
    ).length;
    const liveSpeed = spotlight?.current_speed_mbps || spotlight?.avg_speed_mbps || 0;
    const integrity = summary?.verification?.integrity_rate ?? 100;
    const bytesToday = (recentJobs || [])
        .filter((j) => {
            const ts = j.completed_at || j.started_at || j.created_at;
            if (!ts) return false;
            const d = new Date(ts);
            return d.toDateString() === new Date().toDateString();
        })
        .reduce((a, j) => a + (j.bytes_done || 0), 0);

    const items = [
        {
            label: "Throughput now",
            value: liveSpeed >= 1000
                ? `${(liveSpeed / 1000).toFixed(2)} GB/s`
                : `${liveSpeed.toFixed(1)} MB/s`,
            hint: runningJobs > 0 ? `${runningJobs} active ${runningJobs === 1 ? "job" : "jobs"}` : "Idle",
            color: liveSpeed > 0 ? "#22c55e" : "#6b7280",
            pulse: liveSpeed > 0,
        },
        {
            label: "Data moved today",
            value: formatBytes(bytesToday || 0),
            hint: "Across all tenants' agents",
            color: "#5b5ef0",
        },
        {
            label: "Integrity",
            value: `${integrity.toFixed(1)}%`,
            hint: "SHA-256 verified",
            color: integrity >= 99.5 ? "#22c55e" : integrity >= 95 ? "#f59e0b" : "#ef4444",
        },
        {
            label: "Fleet online",
            value: total > 0 ? `${online}/${total}` : "—",
            hint: total > 0 ? `${fleetPct}% reachable` : "No agents yet",
            color: total === 0 ? "#6b7280" : fleetPct >= 90 ? "#22c55e" : fleetPct >= 60 ? "#f59e0b" : "#ef4444",
        },
        {
            label: "Encryption",
            value: realAgentTotal === 0
                ? "AES-256-GCM"
                : `Quantum-Safe (${pqcReadyCount}/${realAgentTotal})`,
            hint: realAgentTotal === 0
                ? "End-to-end • SHA-256"
                : pqcReadyCount === realAgentTotal
                  ? "All agents hybrid X25519 + ML-KEM-768"
                  : pqcReadyCount > 0
                    ? "Some agents still on classical AES-256-GCM"
                    : "AES-256-GCM • upgrade agents for ML-KEM-768",
            color: realAgentTotal === 0
                ? "#7c3aed"
                : pqcReadyCount === realAgentTotal
                  ? "#22c55e"
                  : pqcReadyCount > 0
                    ? "#f59e0b"
                    : "#7c3aed",
        },
    ];

    return (
        <div
            className="mb-card flex flex-col gap-4 overflow-hidden md:flex-row md:items-stretch md:justify-between md:divide-x md:divide-border"
            data-testid="system-pulse-strip"
        >
            {items.map((it) => (
                <div
                    key={it.label}
                    className="min-w-0 flex-1 px-4 first:pl-0 last:pr-0"
                >
                    <div className="mb-1.5 flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">
                        <span
                            className={[
                                "h-2 w-2 rounded-full",
                                it.pulse ? "animate-pulse-dot" : "",
                            ].join(" ")}
                            style={{ background: it.color }}
                        />
                        {it.label}
                    </div>
                    <div className="font-display text-xl font-bold tracking-tight tabular-nums">
                        {it.value}
                    </div>
                    <div className="mt-0.5 truncate text-[11px] text-muted-foreground">
                        {it.hint}
                    </div>
                </div>
            ))}
        </div>
    );
}

function AgentRow({ color, label, value }) {
    return (
        <div className="flex items-center gap-2">
            <span className="h-2 w-2 rounded-full" style={{ background: color }} />
            <span className="flex-1 text-muted-foreground">{label}</span>
            <span className="font-semibold tabular-nums">{value}</span>
        </div>
    );
}

export function StatusPill({ status }) {
    const map = {
        queued: ["border-[hsl(var(--warning))] text-[hsl(var(--warning))]", "bg-[hsl(var(--warning))]"],
        running: ["border-[hsl(var(--accent))] text-[hsl(var(--accent))]", "bg-[hsl(var(--accent))] animate-pulse-dot"],
        paused: ["border-muted-foreground text-muted-foreground", "bg-muted-foreground"],
        succeeded: ["border-[hsl(var(--success))] text-[hsl(var(--success))]", "bg-[hsl(var(--success))]"],
        completed_with_errors: ["border-[hsl(var(--warning))] text-[hsl(var(--warning))]", "bg-[hsl(var(--warning))]"],
        failed: ["border-[hsl(var(--destructive))] text-[hsl(var(--destructive))]", "bg-[hsl(var(--destructive))]"],
        canceled: ["border-border text-muted-foreground", "bg-muted-foreground"],
    };
    const [cls, dot] = map[status] || ["border-border text-muted-foreground", "bg-muted-foreground"];
    return (
        <span className={`mb-pill ${cls}`} data-testid={`status-pill-${status}`}>
            <span className={`mb-status-dot ${dot}`} />
            {status?.replace(/_/g, " ")}
        </span>
    );
}
